/**
 * 后台管理 - 主布局(侧边栏 + 顶部栏)
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, Users, CreditCard, Database, FileText, Settings, Headphones,
  Menu, X, Bell, Search, LogOut, ChevronRight, Activity, Wifi, WifiOff, RefreshCw, Share2, Trash2, DollarSign, Key, Loader2, Smartphone
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { api, getApiBase, setApiBase } from '../../lib/api.js';
import { DashboardView } from './DashboardView.jsx';
import { UsersView } from './UsersView.jsx';
import { PaymentsView } from './PaymentsView.jsx';
import { DataView } from './DataView.jsx';
import { LogsView } from './LogsView.jsx';
import { SettingsView } from './SettingsView.jsx';
import { SyncStatusView } from './SyncStatusView.jsx';
import { SupportView } from './SupportView.jsx';
import { CommissionsView } from './CommissionsView.jsx';
import { CleanupView } from './CleanupView.jsx';
import { WithdrawalsView } from './WithdrawalsView.jsx';
import { PushView } from './PushView.jsx';
import { SmsView } from './SmsView.jsx';

const MENU = [
  { id: 'dashboard', name: '看板', icon: LayoutDashboard, color: 'text-brand-cyan' },
  { id: 'users', name: '用户管理', icon: Users, color: 'text-brand-purple' },
  { id: 'payments', name: '支付流水', icon: CreditCard, color: 'text-brand-gold' },
  { id: 'commissions', name: '分佣总览', icon: Share2, color: 'text-accent-success' },
  { id: 'withdrawals', name: '提现审核', icon: DollarSign, color: 'text-brand-gold' },
  { id: 'data', name: '数据管理', icon: Database, color: 'text-brand-cyan' },
  { id: 'logs', name: '日志中心', icon: FileText, color: 'text-ink-2' },
  { id: 'support', name: '客服工单', icon: Headphones, color: 'text-brand-cyan' },
  { id: 'push', name: '消息中心', icon: Bell, color: 'text-brand-rose' },
  { id: 'sms', name: 'SMS 管理', icon: Smartphone, color: 'text-brand-cyan' },
  { id: 'sync', name: '同步状态', icon: Database, color: 'text-brand-cyan' },
  { id: 'cleanup', name: '数据清理', icon: Trash2, color: 'text-accent-danger' },
  { id: 'settings', name: '系统设置', icon: Settings, color: 'text-ink-2' },
];

export function AdminLayout() {
  const [token, setToken] = useState(localStorage.getItem('fucai3d_admin_token') || '');
  const [apiBase, setBase] = useState(getApiBase());
  const [authed, setAuthed] = useState(!!token);
  const [view, setView] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [apiStatus, setApiStatus] = useState('checking');  // checking / online / offline
  const [apiLatency, setApiLatency] = useState(null);  // ms延迟
  const [lastCheck, setLastCheck] = useState(0);
  const [pendingTicket, setPendingTicket] = useState(null);  // 待打开的工单 ID(从 PushView 跳转)

  // 探测后端

  // 监听跨组件事件:从 PushView "进入聊天室" → 切到 SupportView + 打开工单
  useEffect(() => {
    const handler = (e) => {
      const tid = e?.detail?.ticketId;
      if (tid) {
        setView('support');
        setPendingTicket(tid);
      }
    };
    window.addEventListener('admin:openTicket', handler);
    return () => window.removeEventListener('admin:openTicket', handler);
  }, []);

  const checkApi = async () => {
    setApiStatus('checking');
    const start = Date.now();
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 5000);
      const res = await fetch(`${getApiBase()}/api/health`, { signal: ctrl.signal });
      clearTimeout(timer);
      setApiStatus(res.ok ? 'online' : 'offline');
      setApiLatency(Date.now() - start);
    } catch {
      setApiStatus('offline');
      setApiLatency(null);
    }
    setLastCheck(Date.now());
  };

  useEffect(() => {
    if (authed) checkApi();
  }, [authed]);

  const handleLogin = () => {
    if (!token) return toast.error('请输入管理员 token');
    localStorage.setItem('fucai3d_admin_token', token);
    setApiBase(apiBase);
    setAuthed(true);
  };

  const handleLogout = () => {
    if (!confirm('确认登出?')) return;
    localStorage.removeItem('fucai3d_admin_token');
    setAuthed(false);
  };

  if (!authed) {
    return (
      <>
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="card max-w-md w-full"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-brand-purple to-brand-cyan flex items-center justify-center">
              <Settings size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold">3D MAX 后台</h1>
              <p className="text-xs text-ink-3">管理员登录</p>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-xs text-ink-3 flex items-center gap-1 mb-1">
                <Activity size={11} /> API 地址
              </label>
              <input
                value={apiBase}
                onChange={e => setBase(e.target.value)}
                placeholder="https://3d-max-api.xxx.workers.dev"
                className="w-full px-3 py-2 bg-bg-base border border-white/10 rounded-lg text-sm font-mono"
              />
            </div>
            <div>
              <label className="text-xs text-ink-3 flex items-center gap-1 mb-1">
                <Settings size={11} /> 管理员 Token
              </label>
              <input
                type="password"
                value={token}
                onChange={e => setToken(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleLogin()}
                placeholder="管理员 token(联系开发者获取)"
                className="w-full px-3 py-2 bg-bg-base border border-white/10 rounded-lg text-sm"
              />
            </div>
            <button onClick={handleLogin} className="w-full btn-primary">
              进入后台 →
            </button>
          </div>

          <div className="mt-4 pt-4 border-t border-white/5 text-xs text-ink-3 space-y-1">
            <div>💡 输入后端 admin 表的 token 登录</div>
            <div>🔧 登录后:系统设置 → 安全 → 轮换 token(自定义你的密码)</div>
            <button
              type="button"
              onClick={() => setShowSetup(true)}
              className="mt-2 text-ink-3 hover:text-brand-cyan"
            >
              🆘 忘记 token?紧急恢复 →
            </button>
          </div>
        </motion.div>
      </div>

      {/* 首次设置 token Modal */}
      {showSetup && <SetupTokenModal
        onClose={() => setShowSetup(false)}
        onSuccess={(t) => {
          setToken(t);
          localStorage.setItem('fucai3d_admin_token', t);
          setAuthed(true);
          setShowSetup(false);
        }}
      />}
    </>
  );
}

  const currentMenu = MENU.find(m => m.id === view);

  return (
    <div className="flex h-screen w-full">
      {/* 侧边栏 - 桌面端固定,移动端抽屉 */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSidebarOpen(false)}
            className="md:hidden fixed inset-0 bg-black/60 z-40"
          />
        )}
      </AnimatePresence>

      <aside className={clsx(
        'flex flex-col bg-bg-card border-r border-white/5 transition-all duration-300 z-50 flex-shrink-0',
        'md:relative',
        sidebarOpen
          ? 'fixed inset-y-0 left-0 translate-x-0 w-52'
          : 'fixed inset-y-0 left-0 -translate-x-full w-52 md:translate-x-0',
        'w-48 md:w-48 lg:w-52'
      )}>
        {/* Logo */}
        <div className="h-12 flex items-center justify-between px-3 border-b border-white/5">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-brand-purple to-brand-cyan flex items-center justify-center font-bold text-xs">
              M
            </div>
            <div>
              <div className="text-sm font-bold leading-none">3D MAX</div>
              <div className="text-[9px] text-ink-3 mt-0.5">Admin Console</div>
            </div>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="md:hidden text-ink-3 hover:text-ink-1">
            <X size={14} />
          </button>
        </div>

        {/* 菜单 */}
        <nav className="flex-1 py-2 px-1.5 overflow-y-auto">
          {MENU.map(m => (
            <button
              key={m.id}
              onClick={() => { setView(m.id); setSidebarOpen(false); }}
              className={clsx(
                'w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[13px] transition mb-0.5',
                view === m.id
                  ? 'bg-white/8 text-white font-medium'
                  : 'text-ink-2 hover:bg-white/4'
              )}
            >
              <m.icon size={14} className={view === m.id ? m.color : ''} />
              <span className="flex-1 text-left">{m.name}</span>
              {view === m.id && <ChevronRight size={11} className="text-brand-cyan" />}
            </button>
          ))}
        </nav>

        {/* 底部状态 */}
        <div className="border-t border-white/5 p-3">
          <ApiStatusCard status={apiStatus} lastCheck={lastCheck} onRefresh={checkApi} apiLatency={apiLatency} />
        </div>
      </aside>

      {/* 主内容 */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* 顶部栏 */}
        <div className="h-11 flex items-center justify-between px-4 border-b border-white/5 bg-bg-card/50 backdrop-blur">
          <div className="flex items-center gap-2 min-w-0">
            <button onClick={() => setSidebarOpen(true)} className="md:hidden text-ink-2">
              <Menu size={18} />
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                {currentMenu && <currentMenu.icon size={13} className={currentMenu.color} />}
                <h1 className="text-[13px] font-semibold truncate">{currentMenu?.name}</h1>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={checkApi}
              className="p-1 rounded hover:bg-white/5 text-ink-3"
              title="刷新后端状态"
            >
              <RefreshCw size={12} />
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1 px-2 py-1 rounded text-[11px] text-ink-3 hover:bg-white/5"
            >
              <LogOut size={11} /> 登出
            </button>
          </div>
        </div>

        {/* 内容区 */}
        <div className="flex-1 overflow-auto p-3 bg-bg-base/50">
          {view === 'dashboard' && <DashboardView />}
          {view === 'users' && <UsersView />}
          {view === 'payments' && <PaymentsView onJumpSettings={(section) => {
            setView('settings');
            // 触发 settings 跳到指定 section
            setTimeout(() => {
              const el = document.getElementById(`section-${section}`);
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
          }} />}
          {view === 'data' && <DataView />}
          {view === 'logs' && <LogsView />}
          {view === 'support' && <SupportView openTicket={pendingTicket} onTicketOpened={() => setPendingTicket(null)} />}
          {view === 'push' && <PushView />}
          {view === 'sms' && <SmsView />}
          {view === 'settings' && <SettingsView />}
          {view === 'commissions' && <CommissionsView />}
          {view === 'withdrawals' && <WithdrawalsView />}
          {view === 'cleanup' && <CleanupView />}
          {view === 'sync' && <SyncStatusView />}
        </div>
      </main>
    </div>
  );
}

function ApiStatusCard({ status, lastCheck, onRefresh, apiLatency = null }) {
  const config = {
    checking: { color: 'text-ink-3', icon: RefreshCw, label: '检测中', bg: 'bg-white/5' },
    online: { color: 'text-brand-cyan', icon: Wifi, label: '在线', bg: 'bg-brand-cyan/10' },
    offline: { color: 'text-accent-danger', icon: WifiOff, label: '离线', bg: 'bg-accent-danger/10' },
  }[status];
  const Icon = config.icon;

  return (
    <div className={clsx('rounded-lg p-2.5', config.bg)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          <Icon size={12} className={clsx(config.color, status === 'checking' && 'animate-pulse')} />
          <span className={clsx('text-xs font-medium', config.color)}>API {config.label}</span>
        </div>
        <button onClick={onRefresh} className="text-ink-3 hover:text-ink-1 flex-shrink-0">
          <RefreshCw size={10} />
        </button>
      </div>
      <div className="text-[10px] text-ink-3 mt-1 truncate" title={getApiBase()}>
        {getApiBase()?.replace(/^https?:\/\//, '') || '(同源)'}
      </div>
      <div className="text-[10px] flex justify-between">
        <span className="text-ink-3">
          {lastCheck ? new Date(lastCheck).toLocaleTimeString('zh-CN') : '—'}
        </span>
        {apiLatency !== null && (
          <span className={clsx(
            'font-mono',
            apiLatency < 50 ? 'text-brand-cyan' : apiLatency < 200 ? 'text-brand-gold' : 'text-accent-danger'
          )}>
            {apiLatency}ms
          </span>
        )}
      </div>
    </div>
  );
}

// 首次部署设置 admin token
function SetupTokenModal({ onClose, onSuccess }) {
  const [newToken, setNewToken] = useState('');
  const [oldToken, setOldToken] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (newToken.length < 8) {
      toast.error('token 至少 8 位');
      return;
    }
    if (confirm !== 'SETUP') {
      toast.error('确认码必须输入 SETUP');
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.admin.tokenSetup({ token: newToken, old_token: oldToken || undefined, confirm });
      if (r?.ok) {
        toast.success('Token 设置成功!');
        onSuccess?.(newToken);
      } else {
        toast.error(r?.error || '设置失败');
      }
    } catch (e) {
      toast.error('设置失败: ' + e.message);
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="card max-w-md w-full"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold flex items-center gap-2">
            <Key size={16} className="text-brand-cyan" /> 设置管理员 Token
          </h2>
          <button onClick={onClose} className="text-ink-3 hover:text-ink-0">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs text-ink-3 mb-1 block">
              新 Token (≥8 位)
            </label>
            <input
              type="password"
              value={newToken}
              onChange={e => setNewToken(e.target.value)}
              placeholder="例如: mySecretAdminPass2026"
              className="w-full px-3 py-2 bg-bg-base border border-white/10 rounded-lg text-sm"
            />
          </div>

          <div>
            <label className="text-xs text-ink-3 mb-1 block">
              旧 Token(必须填正确)
            </label>
            <input
              type="password"
              value={oldToken}
              onChange={e => setOldToken(e.target.value)}
              placeholder="当前有效的 admin token"
              className="w-full px-3 py-2 bg-bg-base border border-white/10 rounded-lg text-sm"
            />
            <div className="text-[10px] text-ink-3 mt-1">
              🔒 已有有效 token:必须提供正确的旧 token(防止别人改)
            </div>
          </div>

          <div>
            <label className="text-xs text-ink-3 mb-1 block">
              确认码(输入 SETUP)
            </label>
            <input
              type="text"
              value={confirm}
              onChange={e => setConfirm(e.target.value.toUpperCase())}
              placeholder="SETUP"
              className="w-full px-3 py-2 bg-bg-base border border-white/10 rounded-lg text-sm font-mono"
            />
          </div>

          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full btn-primary flex items-center justify-center gap-2"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Key size={14} />}
            设置新 Token
          </button>

          <div className="text-[10px] text-amber-400 bg-amber-400/5 border border-amber-400/20 rounded p-2">
            ⚠️ Token 就像密码,丢失后无法找回。建议保存到密码管理器。
          </div>
        </div>
      </motion.div>
    </div>
  );
}
